import { APP_BASE_HREF, PlatformLocation } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule } from "@angular/router";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import {
	AlertModule,
	AlertService,
	AnalyticsModule,
	AnalyticsService,
	ContentReplacementModule,
	PolarisConfigurationService,
	RestModule,
	SessionModule,
	SessionService,
} from "@recore-solutions/nstar-lib";
import { environment } from "src/environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { ConfigurationService } from "./configuration.service";
import { EntityModule } from "./entity.module";
import { HistoryModule } from "./history/history.module";
import { LoadingProgressBarService } from "./shared/services/loading-progress-bar.service";
import { SharedModule } from "./shared/shared.module";
import { Userpilot } from "userpilot";
import { OAuthModule, OAuthService, OAuthStorage } from "angular-oauth2-oidc";
import { authCodeFlowConfig } from "./shared/auth.code-flow.config";
import { SessionIdInterceptor } from "./session-id.interceptor";
import { AuthCallbackComponent } from "../auth-callback.component";
import { LogoutComponent } from "../logout.component";
import { firstValueFrom } from "rxjs";
import { OauthRefreshLeaderService } from "./shared/services/oauth-refresh-leader.service";

export const appInitializerFn = (
	configurationService: ConfigurationService,
	oauthService: OAuthService,
	sessionService: SessionService,
	oauthRefreshLeaderService: OauthRefreshLeaderService
) => {
	return () => {
		oauthService.configure(authCodeFlowConfig);
		let sessionInitFailed = false;

		return oauthService
			.loadDiscoveryDocumentAndTryLogin()
			.then(() => {
				if (oauthService.hasValidAccessToken()) {
					oauthRefreshLeaderService.start();
					return firstValueFrom(sessionService.init()).catch(() => {
						sessionInitFailed = true;
					});
				}
				return Promise.resolve();
			})
			.then(() => {
				if (oauthService.hasValidAccessToken() && !sessionInitFailed) {
					Userpilot.initialize("NX-752c4c8d");
					return firstValueFrom(configurationService.initializeConfiguration()).then(
						() => {},
						() => {}
					);
				}
				return Promise.resolve();
			});
	};
};

export function storageFactory(): OAuthStorage {
	return localStorage;
}

@NgModule({
	declarations: [AppComponent, LogoutComponent, AuthCallbackComponent],
	exports: [NgbModule],
	bootstrap: [AppComponent],
	imports: [
		EntityModule,
		RouterModule,
		BrowserAnimationsModule,
		BrowserModule,
		AppRoutingModule,
		FormsModule,
		ReactiveFormsModule,
		NgbModule,
		SharedModule,
		ContentReplacementModule,
		OAuthModule.forRoot({
			resourceServer: {
				sendAccessToken: true,
			},
		}),
		// HelpModule.forRoot(),
		HistoryModule.forRoot(),
		AlertModule,
		SessionModule.forRoot({
			serviceOfferingName: "Add/Edit",
			appVersion: environment.version,
			endpoint: environment.endpoints.session,
			initializePath: "sessions/initialize",
			savePath: "sessions/member-web-session",
			logoutPath: "logout",
			logoutUrl: environment.endpoints.logout,
		}),
		RestModule.forRoot({
			authenticatedRedirect: "",
			unauthenticatedRedirect: "",
		}),
		AnalyticsModule.forRoot({
			trackingId: environment.google.analytics.trackingId,
		}),
	],
	providers: [
		{ provide: OAuthStorage, useFactory: storageFactory },
		{
			provide: APP_INITIALIZER,
			useFactory: appInitializerFn,
			deps: [ConfigurationService, OAuthService, SessionService, OauthRefreshLeaderService],
			multi: true,
		},
		{
			provide: APP_BASE_HREF,
			useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
			deps: [PlatformLocation],
		},
		{
			provide: PolarisConfigurationService,
			useValue: {
				member: environment.endpoints.member,
				memberCoverage: environment.endpoints.memberCoverage,
			},
		},
		SessionService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: SessionIdInterceptor,
			multi: true,
		},
		AnalyticsService,
		LoadingProgressBarService,
		AlertService,
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}
